// @ts-nocheck

import React, { useState, useMemo } from 'react';
import {
    useId, Input, Label, makeStyles, tokens, Button, Spinner, Dialog,
    DialogTrigger, DialogSurface, DialogTitle, DialogContent, DialogBody,
    DialogActions, Toaster, useToastController, ToastTitle, Toast, ToastIntent,
    Dropdown, Option, // 更改这里: Combobox -> Dropdown
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { BoxSearchRegular } from '@fluentui/react-icons';
import { SearchIcon } from '@primer/octicons-react';

const useStyles = makeStyles({
    base: {
        display: "flex",
        flexDirection: "column",
        '@media (min-width: 768px)': { // 当屏幕宽度大于768px时
            flexDirection: "row",
        },
    },
    field: {
        display: "grid",
        gridRowGap: tokens.spacingVerticalXXS,
        marginTop: tokens.spacingVerticalMNudge,
        padding: tokens.spacingHorizontalMNudge,
    },
    spinner: {
        height: "32px", // 设置按钮和 Spinner 的宽度
        display: 'flex',
        justifyContent: 'center',
    },
    content: {
        display: "flex",
        flexDirection: "column",
        rowGap: "10px",
    },
    dialog: {
        '@media (max-width: 768px)': { // 当屏幕宽度大于768px时
            margin: "auto 5px", // 设置 Dialog 的 margin
        },
    },
});

interface SearchItemProps {
    setSearchResult: (data: any) => void;
}

const SearchItem: React.FC<SearchItemProps> = ({ setSearchResult }) => {
    const { t, i18n } = useTranslation();
    const styles = useStyles();
    const [loading, setLoading] = useState(false);
    const [productId, setProductId] = useState('');
    const [market, setMarket] = useState('');
    const [locale, setLocale] = useState(t('current_locale'));
    const [marketError, setMarketError] = useState<string>('');
    const idPrefix = 'search-item';
    const toastController = useToastController();
    const dispatchToast = (toast: React.ReactElement, options?: { intent: ToastIntent }) => {
        toastController.dispatchToast(toast, options);
    };

    const inputIds = {
        productId: useId(idPrefix + '-product-id'),
        market: useId(idPrefix + '-market'),
        locale: useId(idPrefix + '-locale'),
    };

    interface InputIds {
        productId: string;
        market: string;
        locale: string;
    }

    interface ApiResponse {
        // Define the structure of the API response here
        // For example:
        // id: string;
        // name: string;
        // ...
    }

    const marketOptions = useMemo(() => {
        const regions = i18n.language === 'zh-CN' ? 
            require('../NativeRegions-zh-CN.json') :
            require('../NativeRegions-en-US.json');
            
        return Object.entries(regions).map(([value, label]) => ({
            value,
            label: `${label} (${value})`
        }));
    }, [i18n.language]);

    const localeOptions = useMemo(() => {
        const locales = require('../NativeLocales.json');
        return Object.entries(locales).map(([value, label]) => ({
            value,
            label: `${label} (${value})`  
        }));
    }, []);

    const validateMarket = (value: string) => {
        const isValid = marketOptions.some(option => option.value === value);
        if (!isValid && value) {
            setMarketError(t('invalid_market'));
        } else {
            setMarketError('');
        }
        setMarket(value);
    };

    const handleSearchClick = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        setLoading(true);
        try {
            const response = await fetch(`https://ms-store.apis.caozhi.li/pages/pdp?productId=${productId}&hl=${locale}&gl=${market}`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: ApiResponse = await response.json();

            if (data.errors && data.errors.length > 0) {
                throw new Error(`API error: ${data.errors[0].message}`);
            }

            // Check if pid is for Edge Add-ons
            if (productId.startsWith('0RDCK')) {
                const edgeResponse = await fetch(`https://msedge-addons.apis.caozhi.li/getproductdetails/${productId}?hl=${locale}&gl=${market}`);

                if (!edgeResponse.ok) {
                    throw new Error(`Edge API HTTP error! status: ${edgeResponse.status}`);
                }

                const edgeData: ApiResponse = await edgeResponse.json();

                // Replace averageRating, ratingCount, and lastUpdateDateUtc
                data.averageRating = edgeData.averageRating;
                data.ratingCount = edgeData.ratingCount;
                data.lastUpdateDateUtc = edgeData.lastUpdateDate;

                // Add activeInstallCount, isBadgedAsFeatured
                data.activeInstallCount = edgeData.activeInstallCount;
                data.isBadgedAsFeatured = edgeData.isBadgedAsFeatured;

                // Empty isDownloadable
                data.isDownloadable = null;

                data.supportedLanguages = edgeData.availableLanguages;

                data.catalogSource = data.catalogSource + ' & Edge Add-ons';

                data.crxId = edgeData.crxId;

            }

            setSearchResult(data);
        } catch (error) {
            dispatchToast(
                    <Toast>
                        <ToastTitle>{t('error')}</ToastTitle>
                        {error instanceof Error ? error.message : String(error)}
                    </Toast>,
                    { intent: 'error' }
                );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog>
                <DialogTrigger disableButtonEnhancement>
                    <Button icon={<BoxSearchRegular />}>{t('search')}</Button>
                </DialogTrigger>
                <DialogSurface aria-describedby={undefined} className={styles.dialog}>
                    <form onSubmit={(event) => handleSearchClick(event)}>
                        <DialogBody>
                            <DialogTitle>{t('search')}</DialogTitle>
                            <DialogContent className={styles.content}>
                                <div className={styles.field}>
                                    <Label required htmlFor={inputIds.productId}>{t('search_item_label_product_id')}<br /><small>{t('search_item_label_product_id_example')}</small></Label>
                                    <Input required id={inputIds.productId} value={productId} onChange={(e) => setProductId(e.target.value)} />
                                </div>
                                <div className={styles.field}>
                                    <Label required htmlFor={inputIds.market}>{t('search_item_label_mkt')}<br /><small>{t('search_item_label_mkt_example')}</small></Label>
                                    <Input
                                        required
                                        id={inputIds.market}
                                        value={market}
                                        onChange={(e) => validateMarket(e.target.value)}
                                        validationState={marketError ? 'error' : undefined}
                                        validationMessage={marketError}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <Label required htmlFor={inputIds.locale}>{t('search_item_label_locale')}<br /><small>{t('search_item_label_locale_example')}</small></Label>
                                    <Dropdown
                                        required
                                        clearable
                                        id={inputIds.locale}
                                        value={locale}
                                        onOptionSelect={(e, data) => setLocale(data.optionValue || '')}
                                    >
                                        {localeOptions.map((option) => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <DialogTrigger disableButtonEnhancement>
                                    <Button appearance="secondary">{t('close')}</Button>
                                </DialogTrigger>
                                <Button 
                                type="submit" 
                                appearance="primary" 
                                // style={{ width: '32px' }}
                                >
                                    {loading ? <Spinner size='tiny' /> : <SearchIcon />}
                                </Button>
                            </DialogActions>
                        </DialogBody>
                    </form>
                </DialogSurface>
            </Dialog>
        </>
    );
};

export default SearchItem;
